import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Trash2 } from 'lucide-react';
import { Button } from '@/components/Button';
import { cn } from '@/lib/utils';

/**
 * Selection mode for a list: a "Select" toggle reveals checkboxes, rows become
 * click targets, Shift-click selects ranges. Batch actions run from the bar.
 */
export function useSelection() {
  const [active, setActive] = useState(false);
  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set());
  const lastIdx = useRef<number | null>(null);

  const toggle = (rowId: string, idx: number, orderedIds: string[], shift: boolean) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (shift && lastIdx.current !== null && lastIdx.current !== idx) {
        const a = Math.min(lastIdx.current, idx);
        const b = Math.max(lastIdx.current, idx);
        const adding = !prev.has(rowId);
        for (let i = a; i <= b; i++) {
          if (adding) next.add(orderedIds[i]);
          else next.delete(orderedIds[i]);
        }
      } else if (next.has(rowId)) {
        next.delete(rowId);
      } else {
        next.add(rowId);
      }
      return next;
    });
    lastIdx.current = idx;
  };

  const exit = () => {
    setActive(false);
    setSelected(new Set());
    lastIdx.current = null;
  };

  return { active, selected, toggle, start: () => setActive(true), exit };
}

/**
 * Batch-action bar. Renders in place as the list's last element; while that
 * slot is scrolled out of view, a floating copy is portaled to document.body
 * pinned to the bottom-center of the viewport. The in-place copy stays mounted
 * (but invisible) while floating so the layout height can't collapse — which
 * would make the sentinel oscillate in and out of view.
 */
export function SelectionBar({
  count,
  actionLabel,
  pending,
  onAction,
  onDone,
}: {
  count: number;
  actionLabel: string;
  pending: boolean;
  onAction: () => void;
  onDone: () => void;
}) {
  const slotRef = useRef<HTMLDivElement>(null);
  const [slotInView, setSlotInView] = useState(true);

  useEffect(() => {
    const el = slotRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(([entry]) => setSlotInView(entry.isIntersecting));
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const content = (
    <>
      <span className="text-xs text-muted-foreground">{count} selected</span>
      <div className="flex items-center gap-1.5">
        <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={onDone} disabled={pending}>
          Done
        </Button>
        <Button
          variant="destructive"
          size="sm"
          className="h-7 text-xs"
          disabled={count === 0 || pending}
          onClick={onAction}
        >
          <Trash2 className="h-3 w-3 mr-1" />
          {actionLabel}
        </Button>
      </div>
    </>
  );

  return (
    <div ref={slotRef} className="mt-2">
      <div
        className={cn(
          'flex items-center justify-between rounded-lg border bg-background px-3 py-2 shadow-card',
          !slotInView && 'invisible'
        )}
      >
        {content}
      </div>
      {!slotInView &&
        createPortal(
          <div className="fixed bottom-4 inset-x-0 z-40 flex justify-center px-4 pointer-events-none animate-fade-in">
            <div className="pointer-events-auto flex w-full max-w-xl items-center justify-between rounded-lg border bg-background px-3 py-2 shadow-pop animate-scale-in">
              {content}
            </div>
          </div>,
          document.body
        )}
    </div>
  );
}
